import { v4 as uuidv4 } from 'uuid'
import { ConversationNetworkEvents, INetwork, MetadataMessage, NetworkEventHandlers } from './types'
import { EventEmitter } from '@/ug-core/core/EventEmitter'
import { WebSocketConnection } from './WebSocketConnection'
import { DefaultLogger, StyleGrey } from '../core/Logger'
import { Message } from '../types'
import { ConversationNetworkEventType } from './types'

export class ConversationNetwork
  extends EventEmitter<Record<ConversationNetworkEventType, (...args: any[]) => void>>
  implements INetwork
{
  private wsConnection: WebSocketConnection | null = null
  private sessionId = `${Date.now()}_${uuidv4()}`
  private conversationId: string | null = null
  private isUserFirstMessage = true

  constructor(
    private authToken: string,
    private serverUrl: string,
    private componentId: string,
    private personaId?: string,
    private contextValues?: Record<string, string | number | boolean>,
    private consentToAudioRecording: boolean = true,
    private withAudio: boolean = true
  ) {
    const logger = new DefaultLogger({ category: '🗣️ ConversationNetwork', style: StyleGrey })
    super(logger)
  }

  async connect(): Promise<void> {
    try {
      this.wsConnection = new WebSocketConnection(this.serverUrl, this.getWebSocketHandlers())
      this.wsConnection.connect()
      this.logger.debug('Network connections established - Authenticating next...')
    } catch (error) {
      this.logger.error('Failed to connect', error)
      await this.emit(ConversationNetworkEvents.Error, error)
      throw error
    }
  }

  private getWebSocketHandlers(): NetworkEventHandlers {
    return {
      onOpen: async () => {
        await this.sendInitialConfig()
      },
      onMessage: async (message: Message) => {
        if (message.type === 'metadata') {
          const metadataMessage = message as MetadataMessage
          if (metadataMessage.headers?.['x-conversation-id']) {
            this.conversationId = metadataMessage.headers['x-conversation-id']
            this.logger.info(`📌 Conversation ID set to: ${this.conversationId}`)
          }
        }
        await this.emit(ConversationNetworkEvents.Message, message)
      },
      onError: async (error) => {
        await this.emit(ConversationNetworkEvents.Error, error)
      },
      onClose: async () => {
        await this.emit(ConversationNetworkEvents.Disconnected)
      },
    }
  }

  private async sendInitialConfig(): Promise<void> {
    this.send({ type: 'authorization', token: `Bearer ${this.authToken}` })

    this.send({
      type: 'data',
      data: {
        session_id: this.sessionId,
        with_audio: this.withAudio,
        conversation_id: this.conversationId,
        interrupt_at_char: undefined,
        consent_to_audio_recording: this.consentToAudioRecording,
      },
    })

    this.send({
      type: 'data',
      data: {
        input_audio_format: 'audio/webm',
        ...(this.personaId && { persona_id: this.personaId }),
        ...(this.contextValues && Object.keys(this.contextValues).length > 0
          ? { values: this.contextValues }
          : {}),
      },
    })

    if (this.isUserFirstMessage) {
      this.sendUserFirstMessage()
    }
    await this.emit(ConversationNetworkEvents.Connected)
  }

  private sendUserFirstMessage() {
    this.isUserFirstMessage = false
    this.send({
      type: 'text',
      text: '.',
    })

    this.send({
      type: 'input_complete',
    })
  }

  async disconnect(): Promise<void> {
    if (this.wsConnection) {
      this.wsConnection.disconnect()
      this.wsConnection = null
    }
  }

  async send(data: any): Promise<void> {
    this.logger.debug('send called, type:', data.type)
    if (!this.wsConnection?.isReady()) {
      this.logger.error('WebSocket not ready, cannot send')
      throw new Error('Cant send. WebSocket is not ready')
    }
    this.wsConnection.send(data)
  }

  isReady(): boolean {
    return this.wsConnection?.isReady() || false
  }

  public updateContextValues(newValues: Record<string, string | number | boolean>) {
    this.contextValues = newValues;
  }
}
